// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Input, Space, Tag, Typography } from "antd";
import type { CryptoEngine } from "../crypto";
import { disableLock, isLocked, lockKeyring } from "../keylock";
import { keystoreAvailable } from "../keystore";

// KeyLockPanel surfaces the at-rest key protection. Device keys are ALREADY encrypted at rest by
// default (a non-extractable WebCrypto key — see keystore.ts), with no passphrase. This panel adds
// the OPTIONAL stronger tier: a passphrase, entered once per session, that protects keys even on a
// rooted/stolen device (it removes the browser-sealed slots in favour of a passphrase blob).
export function KeyLockPanel({ engine, engineReady, userId }: { readonly engine: CryptoEngine | null; readonly engineReady: boolean; readonly userId: string }) {
  const { message } = App.useApp();
  const [locked, setLocked] = useState(isLocked(userId)); // passphrase tier on?
  const [sealed, setSealed] = useState<boolean | null>(null); // automatic WebCrypto seal available?
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void keystoreAvailable().then(setSealed);
  }, []);

  async function enable() {
    if (!engine || !pass.trim()) {
      message.warning("Choose a passphrase first");
      return;
    }
    setBusy(true);
    try {
      await lockKeyring(engine, pass.trim(), userId);
      setLocked(true);
      setPass("");
      message.success("Passphrase added — you'll enter it once each session for extra protection");
    } catch (e) {
      message.error(`Couldn't add the passphrase: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!engine || !pass.trim()) {
      message.warning("Enter your passphrase to remove it");
      return;
    }
    setBusy(true);
    try {
      await disableLock(engine, pass.trim(), userId);
      setLocked(false);
      setPass("");
      message.success("Passphrase removed — keys stay encrypted at rest, no more prompts");
    } catch (e) {
      message.error(`Wrong passphrase: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!engineReady) {
    return <Typography.Text type="secondary">Encryption engine is still starting…</Typography.Text>;
  }

  const atRest = locked || sealed === true;
  let methodTag = null;
  if (locked) {
    methodTag = <Tag>passphrase each session</Tag>;
  } else if (sealed === true) {
    methodTag = <Tag>automatic</Tag>;
  }
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Text>
        Status:{" "}
        {atRest ? <Tag color="green">encrypted at rest</Tag> : <Tag color="warning">stored in the clear</Tag>}{" "}
        {methodTag}
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {sealed === true
          ? "Your device keys are encrypted at rest automatically — sealed with a non-extractable key kept in this browser, so localStorage holds only ciphertext and the key can't be copied out. "
          : "This browser can't seal keys automatically (insecure context or no WebCrypto), so add a passphrase to protect them. "}
        Add a passphrase for extra protection even on a rooted or stolen device — you'll enter it once each session.
        The passphrase isn't stored; if you forget it, recover via your key backup. (Neither stops an active site compromise.)
      </Typography.Paragraph>
      <Space.Compact style={{ width: "100%" }}>
        <Input.Password
          className="kl-pass"
          placeholder={locked ? "Passphrase to remove it" : "Passphrase (optional extra protection)"}
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          autoComplete="off"
        />
        {locked ? (
          <Button danger onClick={disable} loading={busy} className="kl-disable">
            Remove passphrase
          </Button>
        ) : (
          <Button type="primary" onClick={enable} loading={busy} className="kl-enable">
            Add passphrase
          </Button>
        )}
      </Space.Compact>
    </Space>
  );
}
